import { useState, type FormEvent, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import type { ClientDetail, ClientInput } from '@/api/catalogApi'
import { Field } from '@/components/common/Field'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormError } from '@/components/admin/catalog/CatalogShared'
import { EMPTY_CLIENT_FORM, addDomains, clientPayload, clientToForm, slugFromName, validateClient, type ClientForm as Form } from '@/lib/catalogForms'

/** Client details: create (New client dialog) or edit (Details tab). Email domains are chips. */
export function ClientForm({
  client,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  client?: ClientDetail
  pending: boolean
  error: unknown
  onSubmit: (input: ClientInput) => void
  onCancel?: () => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState<Form>(() => (client ? clientToForm(client) : EMPTY_CLIENT_FORM))
  const [slugTouched, setSlugTouched] = useState(!!client)
  const [domainText, setDomainText] = useState('')
  const [invalid, setInvalid] = useState<string[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const err = (k: string) => (errors[k] ? t(errors[k]) : null)

  function commitDomains(): Form {
    if (!domainText.trim()) return form
    const res = addDomains(form.emailDomains, domainText)
    const next = { ...form, emailDomains: res.domains }
    setForm(next)
    setInvalid(res.invalid)
    setDomainText(res.invalid.join(' '))
    return next
  }

  function onDomainKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      commitDomains()
    } else if (e.key === 'Backspace' && !domainText && form.emailDomains.length) {
      setForm({ ...form, emailDomains: form.emailDomains.slice(0, -1) })
    }
  }

  const payload = clientPayload(form, client)
  const dirty = !client || Object.keys(payload).length > 0 || !!domainText.trim()

  function submit(e: FormEvent) {
    e.preventDefault()
    const next = commitDomains()
    const found = validateClient(next) as Record<string, string>
    setErrors(found)
    if (Object.keys(found).length === 0) {
      const out = clientPayload(next, client)
      if (!client || Object.keys(out).length) onSubmit(out)
    }
  }

  return (
    <form className="grid max-w-2xl grid-cols-2 gap-4" onSubmit={submit} noValidate>
      <Field id="cl-name" label={t('catalog.clients.name')} error={err('name')}>
        <Input
          id="cl-name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value, ...(slugTouched ? {} : { slug: slugFromName(e.target.value) }) })}
        />
      </Field>
      <Field id="cl-slug" label={t('catalog.clients.slug')} error={err('slug')} help={t('catalog.clients.slugHelp')}>
        <Input
          id="cl-slug"
          className="font-mono"
          value={form.slug}
          onChange={(e) => {
            setSlugTouched(true)
            setForm({ ...form, slug: e.target.value })
          }}
        />
      </Field>
      <Field id="cl-tz" label={t('catalog.clients.timeZone')} error={err('timeZone')} help={t('catalog.clients.timeZoneHelp')} className="col-span-2">
        <Input id="cl-tz" value={form.timeZone} onChange={(e) => setForm({ ...form, timeZone: e.target.value })} />
      </Field>
      <Field
        id="cl-domains"
        label={t('catalog.clients.emailDomains')}
        error={invalid.length ? t('catalog.clients.invalidDomains', { list: invalid.join(', ') }) : null}
        help={t('catalog.clients.emailDomainsHelp')}
        className="col-span-2"
      >
        <div className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
          {form.emailDomains.map((d) => (
            <span key={d} className="inline-flex items-center gap-1 rounded-sm bg-status-neutral-bg px-2 py-[3px] font-mono text-label text-status-neutral-fg">
              {d}
              <button
                type="button"
                aria-label={t('catalog.clients.removeDomain', { domain: d })}
                className="rounded-sm hover:text-ink-strong"
                onClick={() => setForm({ ...form, emailDomains: form.emailDomains.filter((x) => x !== d) })}
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
          <input
            id="cl-domains"
            className="min-w-[160px] grow bg-transparent px-1 font-mono text-meta outline-none placeholder:text-ink-muted"
            placeholder={form.emailDomains.length ? '' : t('catalog.clients.domainPlaceholder')}
            value={domainText}
            onChange={(e) => setDomainText(e.target.value)}
            onKeyDown={onDomainKey}
            onBlur={() => commitDomains()}
          />
        </div>
      </Field>
      <FormError error={error} />
      <div className="col-span-2 flex justify-end gap-2">
        {onCancel ? (
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        ) : null}
        <Button type="submit" disabled={pending || !dirty}>
          {pending ? t('common.saving') : client ? t('common.save') : t('common.create')}
        </Button>
      </div>
    </form>
  )
}
